import { useEffect, useState } from 'react';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import {
  DatePicker,
  LocalizationProvider,
  pickersLayoutClasses,
  PickersLayoutContentWrapper,
  PickersLayoutRoot,
  usePickerActionsContext,
  usePickerContext,
  usePickerLayout,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { dayjs } from 'shared/base';

import { FilterInputProps } from './types';

type Props = FilterInputProps & {
  mode?: 'past' | 'future';
  selectedFilter: string | null;
};

interface QuickSelectOption {
  getDate: () => dayjs.Dayjs;
  label: string;
}

const PAST_OPTIONS: QuickSelectOption[] = [
  { getDate: () => dayjs().subtract(7, 'day'), label: '7 Days Ago' },
  { getDate: () => dayjs().subtract(14, 'day'), label: '14 Days Ago' },
  { getDate: () => dayjs().subtract(30, 'day'), label: '30 Days Ago' },
  { getDate: () => dayjs().subtract(90, 'day'), label: '90 Days Ago' },
];

const FUTURE_OPTIONS: QuickSelectOption[] = [
  { getDate: () => dayjs().endOf('week'), label: 'End of This Week' },
  { getDate: () => dayjs().endOf('month'), label: 'End of This Month' },
  { getDate: () => dayjs().add(90, 'day'), label: 'Next 90 Days' },
  { getDate: () => dayjs().add(180, 'day'), label: 'Next 180 Days' },
];

const getFilterExpression = (filterExpression: string, date: dayjs.Dayjs) => {
  const time = filterExpression.includes('<') ? '23:59:59' : '00:00:00';
  const formattedDate = date.format(`YYYY-MM-DD ${time}`);

  return filterExpression.replace('?', formattedDate);
};

const getValueFromFilterExpression = (expression: string | null): string | null => {
  return expression ? (expression.match(/'([^']+)'/) || [])[1] || null : null;
};

const parseInterval = (str: string, mode: 'past' | 'future'): dayjs.Dayjs | null => {
  const match = str.match(/INTERVAL\s+(\d+)\s+DAY/i);
  if (!match || !match[1]) return null;

  const days = Number.parseInt(match[1]);

  return mode === 'past' ? dayjs().subtract(days, 'day') : dayjs().add(days, 'day');
};

const QuickSelectToolbar = ({ options }: { options: QuickSelectOption[] }) => {
  const { value } = usePickerContext();
  const { setValue } = usePickerActionsContext();
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  const handleClick = (option: QuickSelectOption) => {
    const date = option.getDate();
    setValue(date);
    setSelectedLabel(option.label);
  };

  return (
    <Stack gap={1}>
      {options.map((option) => {
        const isSelected =
          selectedLabel === option.label ||
          (value && value.format('YYYY-MM-DD') === option.getDate().format('YYYY-MM-DD'));

        return (
          <Button
            key={option.label}
            color="primary"
            fullWidth={true}
            onClick={() => handleClick(option)}
            size="small"
            sx={{ fontSize: '10px' }}
            variant={isSelected ? 'contained' : 'outlined'}
          >
            {option.label}
          </Button>
        );
      })}
    </Stack>
  );
};

const DatePickerCustomLayout = (props: any) => {
  const { content, ownerState, toolbar } = usePickerLayout(props);

  return (
    <PickersLayoutRoot className={pickersLayoutClasses.root} ownerState={ownerState}>
      <PickersLayoutContentWrapper
        className={pickersLayoutClasses.contentWrapper}
        ownerState={ownerState}
      >
        <Stack alignItems="center" direction="row" px={1}>
          <div>{toolbar}</div>
          <div>{content}</div>
        </Stack>
      </PickersLayoutContentWrapper>
    </PickersLayoutRoot>
  );
};

export const ReportDateFilterSelect = ({
  defaultValue,
  filterExpression = `event_time >= '?'`,
  filterLabel = 'Date',
  mode = 'past',
  onChange,
  selectedFilter,
}: Props) => {
  const selectedDate = getValueFromFilterExpression(selectedFilter);

  useEffect(() => {
    const source = selectedDate || defaultValue;

    if (!source) return;

    if (/INTERVAL\s+(\d+)\s+DAY/i.test(source)) {
      const parsedDate = parseInterval(source, mode);
      if (parsedDate) {
        onChange(getFilterExpression(filterExpression, parsedDate));
      }
    } else if (!selectedDate && defaultValue) {
      const date = dayjs(defaultValue);
      if (!date.isValid()) {
        return;
      }

      onChange(getFilterExpression(filterExpression, date));
    }
  }, [selectedDate, defaultValue, filterExpression, onChange, mode]);

  const value = selectedDate ? dayjs(selectedDate) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} dateFormats={{ keyboardDate: 'YYYY-MM-DD' }}>
      <DatePicker
        label={filterLabel}
        onChange={(date) => {
          onChange(date ? getFilterExpression(filterExpression, date) : null);
        }}
        slotProps={{
          textField: {
            InputProps: {
              disableUnderline: true,
              style: {
                fontSize: 12,
                height: 48,
              },
            },
            sx: {
              maxWidth: 160,
            },
          },
        }}
        slots={{
          layout: DatePickerCustomLayout,
          toolbar: () => (
            <QuickSelectToolbar options={mode === 'past' ? PAST_OPTIONS : FUTURE_OPTIONS} />
          ),
        }}
        value={value}
      />
    </LocalizationProvider>
  );
};
